import type { FieldError } from "./envelope";

/**
 * Kanonik hata kodları (CLOS-306).
 *
 * Kod, istemcinin DAVRANIŞ kararını verdiği yerdir; kullanıcıya gösterilen metin değil.
 * Aynı hata sınıfı her endpoint'te aynı kodu kullanır — bir uç "yetkiniz yok"a FORBIDDEN,
 * diğeri CONFLICT deseydi istemci her uç için ayrı dal yazmak zorunda kalırdı.
 */
export const ERROR_CODES = {
  // Girdi
  VALIDATION: "VALIDATION_ERROR",
  BAD_REQUEST: "BAD_REQUEST",
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  // Kimlik ve yetki
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  /**
   * Organizasyon 2FA zorunlu kılıyor ve kullanıcının ikinci faktörü yok (CLOS-292 `[046]`).
   *
   * ⚠ `FORBIDDEN`'dan AYRI bir koddur ve bu bilinçli: "yetkin yok" ile "bir adım eksik"
   * kullanıcı için taban tabana zıt. `FORBIDDEN` dönseydi `[044]`'ün güvenli geri dönüşü
   * bunu ERİŞİM KAYBI sanıp kullanıcıyı başka bir çalışma alanına atardı — oysa yapması
   * gereken tek şey 2FA kurmak.
   */
  TWO_FACTOR_REQUIRED: "TWO_FACTOR_REQUIRED",
  NOT_FOUND: "NOT_FOUND",
  // Çakışma ailesi — dördü AYRI durumlardır ve istemci her birine farklı tepki verir
  CONFLICT: "CONFLICT",
  CONCURRENCY_CONFLICT: "CONCURRENCY_CONFLICT",
  IDEMPOTENCY_CONFLICT: "IDEMPOTENCY_CONFLICT",
  IDEMPOTENCY_IN_PROGRESS: "IDEMPOTENCY_IN_PROGRESS",
  /**
   * Bir entegrasyon KALICI olarak yapılandırılmamış/kapalı (ör. iyzico'nun kabiliyetleri
   * henüz doğrulanmadı — KARAR 03, `lib/billing/iyzico.ts`).
   *
   * ⚠ `PROVIDER_ERROR`'dan (502) AYRI ve BİLEREK 5xx DEĞİL: 502 "sağlayıcıya ulaşıldı ama
   * o hata döndürdü" — geçici, tekrar denenebilir bir dış arıza demek. Burası tam tersi:
   * sağlayıcıya HİÇ ulaşılmadı, kapı bilerek kapalı ve durum kalıcı. Kimliksiz/bozuk bir
   * isteğin bu koda çarpması bir kusur DEĞİLDİR (sunucu hatası sayılmaz); istemcinin
   * körlemesine tekrar denemesi bir şeyi değiştirmez.
   */
  NOT_CONFIGURED: "NOT_CONFIGURED",
  // Kapasite
  QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
  RATE_LIMITED: "RATE_LIMITED",
  // Dış dünya ve beklenmeyen
  PROVIDER_ERROR: "PROVIDER_ERROR",
  INTERNAL: "INTERNAL",
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * Hata zarfının sürümü. İstemci "bu zarfı anlıyor muyum?" sorusunu ancak buradan
 * cevaplayabilir; sürüm olmadan kırıcı bir değişiklik sessizce yanlış davranışa döner.
 */
export const ERROR_ENVELOPE_VERSION = 1;

export type ErrorCatalogEntry = {
  /** HTTP durum kodu. Transport durumu ile domain kodu tutarlı olmak zorundadır. */
  status: number;
  /**
   * Yerelleştirme anahtarı. Kullanıcıya gösterilecek metni İSTEMCİ üretir; sunucudan
   * gelen serbest metin doğrudan basılmaz (CLOS-217). Zarftaki `message` alanı yalnız
   * güvenli bir yedektir.
   */
  messageKey: string;
  /**
   * İstemci bu hatayı aynı istekle güvenle tekrar deneyebilir mi?
   * Kullanıcı hataları için `false`: aynı bozuk girdi aynı sonucu verir ve istemciyi
   * boş yere döngüye sokmak hem sunucuyu hem kullanıcıyı yorar.
   */
  retryable: boolean;
  /** Geliştirici için: bu kod ne zaman kullanılır. */
  description: string;
};

/**
 * Tek gerçek kaynak: HTTP kodu, mesaj anahtarı ve yeniden deneme bilgisi kodun yanında
 * yaşar. İki yerde tutulsaydı biri güncellenir, diğeri unutulurdu.
 */
export const ERROR_CATALOG: Record<ErrorCode, ErrorCatalogEntry> = {
  [ERROR_CODES.VALIDATION]: {
    status: 422,
    messageKey: "hata.dogrulama",
    retryable: false,
    description: "Girdi şemaya uymuyor. `fields` alan bazlı hataları taşır.",
  },
  [ERROR_CODES.BAD_REQUEST]: {
    status: 400,
    messageKey: "hata.bozuk_istek",
    retryable: false,
    description: "İstek gövdesi hiç ayrıştırılamadı (bozuk JSON, eksik content-type).",
  },
  [ERROR_CODES.PAYLOAD_TOO_LARGE]: {
    status: 413,
    messageKey: "hata.istek_cok_buyuk",
    retryable: false,
    description: "İstek gövdesi boyut sınırını aştı.",
  },
  [ERROR_CODES.UNAUTHORIZED]: {
    status: 401,
    messageKey: "hata.oturum_gerekli",
    retryable: false,
    description: "Oturum yok ya da geçersiz. İstemci giriş ekranına yönlendirir.",
  },
  [ERROR_CODES.FORBIDDEN]: {
    status: 403,
    messageKey: "hata.yetki_yok",
    retryable: false,
    description: "Kimlik doğrulandı ama bu işleme yetki yok.",
  },
  [ERROR_CODES.TWO_FACTOR_REQUIRED]: {
    status: 403,
    messageKey: "hata.iki_faktor_gerekli",
    retryable: false,
    description: "Organizasyon iki aşamalı doğrulama zorunlu kılıyor; kurulum tamamlanmalı.",
  },
  [ERROR_CODES.NOT_FOUND]: {
    status: 404,
    messageKey: "hata.bulunamadi",
    retryable: false,
    description:
      "Kayıt yok VEYA kullanıcı görme yetkisine sahip değil. Ayrım bilinçli olarak " +
      "gizlenir (CLOS-308: varlık sızıntısı).",
  },
  [ERROR_CODES.CONFLICT]: {
    status: 409,
    messageKey: "hata.cakisma",
    retryable: false,
    description: "Domain kuralı ihlali (ör. aynı ada sahip kayıt zaten var).",
  },
  [ERROR_CODES.CONCURRENCY_CONFLICT]: {
    status: 409,
    messageKey: "hata.eszamanli_degisiklik",
    retryable: false,
    description:
      "Kayıt, kullanıcı onu okuduktan sonra başkası tarafından değiştirildi. İstemci " +
      "yeniden okuyup kullanıcıya farkı göstermelidir — körlemesine tekrar denemek " +
      "başkasının değişikliğini sessizce ezer.",
  },
  [ERROR_CODES.IDEMPOTENCY_CONFLICT]: {
    status: 409,
    messageKey: "hata.idempotency_cakismasi",
    retryable: false,
    description:
      "Aynı idempotency anahtarı FARKLI bir payload ile geldi. Tekrar denemek aynı " +
      "sonucu verir; istemcinin anahtarı yenilemesi gerekir.",
  },
  [ERROR_CODES.IDEMPOTENCY_IN_PROGRESS]: {
    status: 409,
    messageKey: "hata.islem_devam_ediyor",
    retryable: true,
    description:
      "Aynı anahtarla gelen önceki istek hâlâ işleniyor. Kısa bir bekleme sonrası " +
      "aynı istek güvenle tekrarlanabilir.",
  },
  [ERROR_CODES.NOT_CONFIGURED]: {
    status: 409,
    messageKey: "hata.yapilandirilmamis",
    retryable: false,
    description:
      "Entegrasyon kalıcı olarak kapalı (kabiliyetler doğrulanmadı). 502 DEĞİL — " +
      "sağlayıcıya hiç ulaşılmadı ve tekrar denemek durumu değiştirmez.",
  },
  [ERROR_CODES.QUOTA_EXCEEDED]: {
    status: 403,
    messageKey: "hata.kota_asildi",
    retryable: false,
    description: "Lisans/paket kapasitesi doldu. Zamanla çözülmez; plan değişmelidir.",
  },
  [ERROR_CODES.RATE_LIMITED]: {
    status: 429,
    messageKey: "hata.cok_fazla_istek",
    retryable: true,
    description: "İstek hızı sınırı aşıldı. `retry.afterSeconds` bekleme süresini verir.",
  },
  [ERROR_CODES.PROVIDER_ERROR]: {
    status: 502,
    messageKey: "hata.saglayici",
    retryable: true,
    description: "Dış sağlayıcı hata döndürdü. Sağlayıcının iç cevabı istemciye ASLA aktarılmaz.",
  },
  [ERROR_CODES.INTERNAL]: {
    status: 500,
    messageKey: "hata.beklenmeyen",
    retryable: false,
    description:
      "Beklenmeyen sistem hatası. `retryable: false` çünkü 500 geçici de olabilir " +
      "kalıcı da; istemcinin körlemesine tekrarı bir arızayı yük saldırısına çevirir.",
  },
};

export function errorEntry(code: ErrorCode): ErrorCatalogEntry {
  return ERROR_CATALOG[code];
}

/**
 * ⚠ `isRetryable(code)` BURADA YOK — CLOS-845 ile kaldırıldı.
 *
 * Hiçbir yerden çağrılmıyordu: hem sunucu zarfı (`middleware/errorHandler.ts`) hem
 * istemci `errorEntry(code).retryable` okuyor. Aynı bilgiye iki yol, biri ölü — ve ölü
 * olan, bir gün katalogla ayrışabilecek olandı.
 */

/** Güvenli yeniden deneme bilgisi — zarfın `retry` alanına konur. */
export type RetryInfo = { retryable: boolean; afterSeconds?: number };

export class AppError extends Error {
  constructor(
    public code: ErrorCode,
    message: string, // Türkçe, kullanıcıya gösterilebilir güvenli yedek metin
    public status: number,
    public fields?: FieldError[],
    /** RATE_LIMITED gibi durumlarda bekleme süresi. */
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "AppError";
  }
}

/**
 * Kataloğun HTTP eşlemesini kullanarak hata üretir. Çağıran yerin durum kodunu elle
 * yazması, eşlemenin iki yere dağılması demektir.
 */
export function appError(
  code: ErrorCode,
  message: string,
  options: { fields?: FieldError[]; retryAfterSeconds?: number } = {},
): AppError {
  return new AppError(
    code,
    message,
    errorEntry(code).status,
    options.fields,
    options.retryAfterSeconds,
  );
}

export function notFound(message = "Kayıt bulunamadı"): AppError {
  return appError(ERROR_CODES.NOT_FOUND, message);
}
export function forbidden(message = "Bu işlem için yetkiniz yok"): AppError {
  return appError(ERROR_CODES.FORBIDDEN, message);
}
export function conflict(message: string): AppError {
  return appError(ERROR_CODES.CONFLICT, message);
}
export function unauthorized(message = "Oturum gerekli"): AppError {
  return appError(ERROR_CODES.UNAUTHORIZED, message);
}
/**
 * ⚠ `concurrencyConflict()` BURADA YOK — CLOS-845 ile kaldırıldı.
 *
 * `CONCURRENCY_CONFLICT` fırlatan dokuz yerin HEPSİ kendi domain mesajını veriyor
 * (`CLIENT_STALE_VERSION_MESSAGE`, sözleşme içeriği mesajı, ...): kullanıcı neyin
 * değiştiğini o cümleden okuyor. Yardımcının tek katkısı bir VARSAYILAN metindi ve o
 * varsayılan hiç kullanılmadı. Kalsaydı, aynı iş için biri hiç yürünmeyen iki yol olurdu.
 *
 * `notFound`/`forbidden`/`unauthorized` bunun tersi: onların varsayılanı çağıranların
 * çoğunda GERÇEKTEN kullanılıyor.
 */
export function quotaExceeded(message: string): AppError {
  return appError(ERROR_CODES.QUOTA_EXCEEDED, message);
}
/**
 * Dış sağlayıcı hatası (502).
 *
 * ⚠ Bir süre bu yardımcı ölü duruyordu ve tek fırlatma noktası (sosyal giriş) durum kodunu
 * ELLE yazıyordu — `appError`'ın engellemek için var olduğu şey (CLOS-845). Katalog 502'yi
 * zaten biliyor; çağıran yerin de bilmesi eşlemeyi iki yere dağıtır.
 */
export function providerError(message = "Dış servise ulaşılamadı"): AppError {
  return appError(ERROR_CODES.PROVIDER_ERROR, message);
}
/**
 * Kalıcı olarak kapalı entegrasyon (409, `providerError`'dan AYRI — katalog gerekçesi).
 */
export function notConfigured(message = "Bu özellik henüz kullanılamıyor"): AppError {
  return appError(ERROR_CODES.NOT_CONFIGURED, message);
}
